






















































































































































import { computed, defineComponent, PropType } from '@nuxtjs/composition-api';
import useBrandConfig from '@shared/composables/useBrandConfig';
import getIPXUrl from '@shared/helpers/getIPXUrl';
import { getProductTags } from '@shared/helpers/product/getProductTags';
import { BaseButton, BaseImage, UCIcon } from '@shared/ui';

import {
  PAGE_ROUTES,
  PRODUCT_CARD_ALT_IMG,
  SPECIAL_ORDER_BADGE_COPY,
  STOCK_STATUS,
} from '~/constants';

import Tag from './Tag.vue';
import { IProductCardProduct, IProductCardSize } from './utils/types';

interface IProductCardViewFooter {
  text: string;
  icon?: string;
  class: string;
  link?: string;
  linkText?: string;
}

export default defineComponent({
  name: 'ProductCardView',
  components: {
    UCIcon,
    BaseButton,
    BaseImage,
    Tag,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object as PropType<IProductCardProduct>,
      required: true,
    },
    showFitment: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<IProductCardSize>,
      default: 'standard',
    },
    selectItemAnalytics: {
      type: Function as PropType<() => void>,
      required: true,
    },
    addToCart: {
      type: Function as PropType<() => void>,
      required: true,
    },
    showActionButton: {
      type: Boolean,
      default: false,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup({ product, size, showFitment, selectItemAnalytics, addToCart, showActionButton }) {
    const { componentConfig, globalConfig } = useBrandConfig();
    const config = componentConfig.ProductCard;

    const { hasVariants, availability, isBackOrder, isSpecialOrder } = product;

    const supportText = computed(() => {
      if (config.showContactStoreMessage) {
        return '';
      }

      return hasStock.value
        ? STOCK_STATUS.SUPPORT_TEXT_WITH_STOCK
        : STOCK_STATUS.SUPPORT_TEXT_NO_STOCK;
    });

    const handleFooterStyles = (): IProductCardViewFooter => {
      const baseClass = 'wrapper-stock-status';

      if (config.showContactStoreMessage) {
        return {
          text: STOCK_STATUS.CONTACT_STORE_SUFFIX,
          linkText: STOCK_STATUS.CONTACT_STORE_LINK_LABEL,
          icon: 'fa-location-dot',
          class: `${baseClass} ${baseClass}--contact-store`,
          link: PAGE_ROUTES.store,
        };
      }

      if (isSpecialOrder) {
        return {
          text: SPECIAL_ORDER_BADGE_COPY || 'Special Order',
          icon: 'fa-circle-check',
          class: `${baseClass} ${baseClass}--delayed-order`,
        };
      }

      if (isBackOrder) {
        return {
          text: 'Backorder',
          icon: 'fa-circle-check',
          class: `${baseClass} ${baseClass}--delayed-order`,
        };
      }

      if (availability === 'made-to-order') {
        return {
          text: 'Made to Order',
          icon: 'fa-hammer',
          class: `${baseClass} ${baseClass}--made-to-order`,
        };
      }

      if (availability === 'out-of-stock') {
        return {
          text: STOCK_STATUS.OUT_OF_STOCK,
          icon: 'fa-circle-xmark',
          class: `${baseClass} ${baseClass}--out-of-stock`,
        };
      }

      return {
        text: STOCK_STATUS.IN_STOCK,
        icon: 'fa-circle-check',
        class: `${baseClass} ${baseClass}--in-stock`,
      };
    };

    const hasStock = computed(() => product.availability === 'in-stock');

    const ariaLabel = computed(() => `View ${product.name}`);

    const tags = computed(() => {
      return getProductTags(
        product.price,
        product.compareToPrice,
        product.tags,
        globalConfig.supportPromotions,
      );
    });

    const showPartNumber = computed(
      () => config.showPartNumber && size !== 'mini' && product.partNumber,
    );

    const displayFitment = computed(() => showFitment && size !== 'mini');

    const brandLogo = getIPXUrl(PRODUCT_CARD_ALT_IMG, 150);

    const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

    const actionButton = computed(() => {
      if (size === 'mini' && !showActionButton) return null;

      const viewDetailsActionButton = {
        label: 'View Details',
        ariaLabel: 'view details',
        disabled: false,
        link: product.link,
        onClick: null,
        icon: null,
      };

      // Always View Details (config override)
      if (config.alwaysShowViewDetails) {
        return viewDetailsActionButton;
      }

      // Out of Stock
      if (config.showOutOfStock && (product.hasVariants || !hasStock.value)) {
        return {
          label: 'Out of Stock',
          ariaLabel: 'out of stock',
          disabled: true,
          link: product.link,
          onClick: null,
          icon: null,
        };
      }

      // View Details
      if (product.hasVariants || !hasStock.value) {
        return viewDetailsActionButton;
      }

      // Add to Cart
      if (!product.hasVariants && hasStock.value) {
        return {
          label: 'Add to Cart',
          ariaLabel: 'add to cart',
          disabled: false,
          link: null,
          onClick: addToCart,
          icon: 'fa-solid fa-cart-plus',
        };
      }
    });

    const onActionButtonLinkClick = () => {
      if (actionButton.value?.link && !actionButton.value.disabled) selectItemAnalytics();
    };

    const hideBrand = computed(() => config.hideBrand);

    const priceRange = computed(() =>
      config.showDigitalPriceRange && product.maxPrice && product.maxPrice > product.price
        ? `${formatCurrency(product.price)} - ${formatCurrency(product.maxPrice)}`
        : null,
    );

    return {
      tags,
      hasVariants,
      showPartNumber,
      ariaLabel,
      hideBrand,
      showSavingsTag: config.showSavingsTag,
      showCompareAtPrice: config.showCompareAtPrice,
      showStockStatusOnly: config.showStockStatusOnly,
      hideStockStatus: config.hideStockStatus,
      actionButton,
      onActionButtonLinkClick,
      brandLogo,
      displayFitment,
      formatCurrency,
      handleFooterStyles,
      supportText,
      priceRange,
    };
  },
});
